import { create } from 'zustand';
import { db, auth } from '../lib/firebase';
import useVibeStore from './useVibeStore';
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    doc,
    updateDoc,
    getDoc,
    getDocs,
    deleteDoc,
    writeBatch,
    where,
    limit,
    startAfter
} from 'firebase/firestore';
import { NodeService } from '../services/NodeService';

const useChatStore = create((set, get) => ({
    standaloneChats: [],
    unsubscribeStandaloneChats: null,
    unsubscribeMessages: null,
    unsubscribeActiveChat: null,
    lastStandaloneChatDoc: null,
    hasMoreStandaloneChats: true,
    activeChat: null,
    messages: [],
    isAiThinking: false,
    loading: false,
    error: null,

    setActiveChat: async (sessionId) => {
        if (!sessionId) return;

        // 1. Fetch Session
        try {
            const sessionSnap = await getDoc(doc(db, 'sessions', sessionId));
            if (!sessionSnap.exists()) {
                console.error("Session not found");
                return;
            }
            const sessionData = { id: sessionSnap.id, ...sessionSnap.data() };

            // 2. Fetch Node Context (if it belongs to a Node)
            let nodeData = {};
            let ancestry = [];
            if (sessionData.nodeId) {
                nodeData = await NodeService.getNode(sessionData.nodeId);
                ancestry = await NodeService.getNodeAncestry(sessionData.nodeId);
            }

            // 3. Resolve Model Inheritance
            let effectiveModelConfigId = sessionData.modelConfigId || null;

            if (!effectiveModelConfigId && ancestry.length > 0) {
                // Ancestry is ordered [Root, ..., Parent, CurrentNode]
                // Search from current node upwards
                for (let i = ancestry.length - 1; i >= 0; i--) {
                    if (ancestry[i].settings?.modelConfigId) {
                        effectiveModelConfigId = ancestry[i].settings.modelConfigId;
                        break;
                    }
                }
            }

            // 4. Construct Active Chat Object (Initial)
            const getDerivedChat = (data) => ({
                ...data,
                node: nodeData || null,
                modelConfigId: effectiveModelConfigId,
                expertName: nodeData?.contextData?.expertName || data.title || "Chat",
                expertAvatar: nodeData?.contextData?.expertAvatar || '',
                participants: data.participants || nodeData?.members || [auth.currentUser?.uid],
                desktopEnabled: data.desktopEnabled === true || nodeData?.settings?.desktopEnabled === true || nodeData?.type === 'project',
                localBrainEnabled: data.localBrainEnabled || false,
                targetDeviceId: data.targetDeviceId || nodeData?.settings?.targetDeviceId || "",
            });

            set({ activeChat: getDerivedChat({ id: sessionSnap.id, ...sessionSnap.data() }) });

            // 5. Subscribe to Session Updates (Reactive)
            const oldUnsub = get().unsubscribeActiveChat;
            if (oldUnsub) oldUnsub();

            const unsub = onSnapshot(doc(db, 'sessions', sessionId), (docSnap) => {
                if (docSnap.exists()) {
                    set({ activeChat: getDerivedChat({ id: docSnap.id, ...docSnap.data() }) });
                }
            });
            set({ unsubscribeActiveChat: unsub });

        } catch (err) {
            console.error("Error setting active session:", err);
        }
    },

    // Subscribe to messages within a specific Session
    subscribeToMessages: (sessionId) => {
        if (!sessionId) return () => { };

        set({ loading: true, messages: [] });

        const oldUnsub = get().unsubscribeMessages;
        if (oldUnsub) oldUnsub();

        const q = query(
            collection(db, 'sessions', sessionId, 'messages'),
            orderBy('createdAt', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Handle isAiThinking state
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg) {
                if (lastMsg.senderId === 'AI') {
                    // Logic: If AI message is new, and NOT marked as isThinking (for tools), then thinking is DONE
                    if (lastMsg.isThinking !== true) {
                        set({ isAiThinking: false });
                    }
                }

                // --- Vibe Sync: Look for latest vibe_result to sync global intervention wait state
                // Walk backwards to find the most recent vibe result, if any
                for (let i = msgs.length - 1; i >= 0; i--) {
                    const m = msgs[i];
                    if (m.action && m.action.type === 'vibe_result') {
                        const { status, image } = m.action.data;
                        if (
                            status === 'waiting_for_proceed' ||
                            status === 'waiting_for_run_terminal' ||
                            status === 'waiting_for_accept_terminal' ||
                            status === 'waiting_for_accept_changes'
                        ) {
                            useVibeStore.getState().setPendingAction(status, sessionId, image);
                        } else if (status === 'completed' || status === 'timeout') {
                            // If the MOST RECENT vibe result is completed/timeout, clear global state
                            useVibeStore.getState().clearPendingAction();
                        }
                        break; // Only care about the absolutely most recent one in the chat
                    }
                }
            }

            set({ messages: msgs, loading: false });
        }, (err) => {
            console.error("Messages subscription error:", err);
            set({ error: err.message, loading: false });
        });

        set({ unsubscribeMessages: unsubscribe });
        return unsubscribe;
    },

    sendMessage: async (sessionId, text, options = {}) => {
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("No user");

            set({ isAiThinking: true });

            // 1. Add message to subcollection
            await addDoc(collection(db, 'sessions', sessionId, 'messages'), {
                text,
                senderId: user.uid,
                createdAt: serverTimestamp(),
                ...options // Spread options (e.g., isDirectCommand)
            });

            // 2. Update the parent session document
            const sessionRef = doc(db, 'sessions', sessionId);
            await updateDoc(sessionRef, {
                lastMessage: text,
                lastActive: serverTimestamp(),
                [`readStatus.${user.uid}`]: serverTimestamp()
            });

        } catch (err) {
            console.error(err);
            set({ error: err.message });
        }
    },

    markChatAsRead: async (sessionId) => {
        try {
            const user = auth.currentUser;
            if (!user) return;

            const sessionRef = doc(db, 'sessions', sessionId);
            await updateDoc(sessionRef, {
                [`readStatus.${user.uid}`]: serverTimestamp()
            });
        } catch (err) {
            console.error("Error marking read:", err);
        }
    },

    updateChatSettings: async (sessionId, settings) => {
        try {
            const sessionRef = doc(db, 'sessions', sessionId);
            await updateDoc(sessionRef, settings);

            const active = get().activeChat;
            if (active && active.id === sessionId) {
                set({ activeChat: { ...active, ...settings } });
            }
        } catch (err) {
            console.error("Error updating session settings:", err);
            throw err;
        }
    },

    subscribeToStandaloneChats: (limitCount = 15) => {
        const user = auth.currentUser;
        if (!user) return;

        set({ loading: true, standaloneChats: [], lastStandaloneChatDoc: null, hasMoreStandaloneChats: true });

        const oldUnsub = get().unsubscribeStandaloneChats;
        if (oldUnsub) oldUnsub();

        const q = query(
            collection(db, 'sessions'),
            where('nodeId', '==', null),
            where('participants', 'array-contains', user.uid),
            orderBy('lastActive', 'desc'),
            limit(limitCount)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const standalone = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const lastDoc = snapshot.docs[snapshot.docs.length - 1];
            set({
                standaloneChats: standalone,
                loading: false,
                lastStandaloneChatDoc: lastDoc,
                hasMoreStandaloneChats: snapshot.docs.length === limitCount
            });
        }, (err) => {
            console.error("Standalone chats subscription error:", err);
            set({ error: err.message, loading: false });
        });

        set({ unsubscribeStandaloneChats: unsubscribe });
        return unsubscribe;
    },

    fetchMoreStandaloneChats: async (limitCount = 15) => {
        const user = auth.currentUser;
        const lastDoc = get().lastStandaloneChatDoc;
        const hasMore = get().hasMoreStandaloneChats;

        if (!user || !lastDoc || !hasMore) return;

        set({ loading: false }); // We don't want a global loading spinner for "Load More"

        try {
            const q = query(
                collection(db, 'sessions'),
                where('nodeId', '==', null),
                where('participants', 'array-contains', user.uid),
                orderBy('lastActive', 'desc'),
                startAfter(lastDoc),
                limit(limitCount)
            );

            const snapshot = await getDocs(q);
            const moreChats = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const newLastDoc = snapshot.docs[snapshot.docs.length - 1];
            const currentChats = get().standaloneChats;

            set({
                standaloneChats: [...currentChats, ...moreChats],
                lastStandaloneChatDoc: newLastDoc || lastDoc,
                hasMoreStandaloneChats: snapshot.docs.length === limitCount
            });
        } catch (err) {
            console.error("Error fetching more chats:", err);
            set({ error: err.message });
        }
    },

    createChat: async (title, initialSettings = {}, nodeId = null) => {
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("No user");

            return await NodeService.createSession(user.uid, nodeId, title, initialSettings);
        } catch (err) {
            console.error("Error creating chat:", err);
            throw err;
        }
    },

    deleteChat: async (sessionId) => {
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("No user");

            const sessionRef = doc(db, 'sessions', sessionId);
            const sessionSnap = await getDoc(sessionRef);
            if (!sessionSnap.exists()) return;

            // 1. Delete all messages in the session
            const messagesRef = collection(db, 'sessions', sessionId, 'messages');
            const messagesSnap = await getDocs(messagesRef);

            const batch = writeBatch(db);
            messagesSnap.forEach((messageDoc) => {
                batch.delete(messageDoc.ref);
            });

            // 2. Delete the session document itself
            batch.delete(sessionRef);

            await batch.commit();

            // 3. Clear active chat if it was the one deleted
            const active = get().activeChat;
            if (active && active.id === sessionId) {
                set({ activeChat: null, messages: [] });
            }
        } catch (err) {
            console.error("Error deleting chat:", err);
            throw err;
        }
    }

}));

export default useChatStore;
